<template>
  <div
    :id="id"
    data-testid="tabs-component">
    <div
      role="tablist"
      :class="[tabListClasses, 'overflow-x-auto whitespace-nowrap']"
      v-bind="ariaLabelAttr">
      <button
        v-for="(tab, index) in slottedTabs"
        :key="index"
        role="tab"
        :id="tabIds[index]"
        :aria-selected="isSelectedTab(index)"
        :aria-controls="panelIds[index]"
        :tabindex="setTabIndex(index)"
        @click="setSelectedTab(index, $event)"
        @keydown="changeFocus(index, $event)"
        ref="tabs"
        :class="getTabClasses(index)"
      >
        <component :is="tab">
        </component>
      </button>
    </div>
    <div :class="panelWrapperCssClasses">
      <div
        v-for="(_, index) in slottedTabs"
        :key="index"
        role="tabpanel"
        :id="panelIds[index]"
        :aria-labelledby="tabIds[index]"
        :class="getPanelClasses(index)"
        v-show="isSelectedTab(index)"
      >
        <slot :name="`tab${index}`" >
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TabsComponent',
  props: {
    /** Id of component wrapper. */
    id: {
      type: String,
      required: true,
    },
    /** */
    ariaLabel: {
      type: String,
      default: null,
    },
    ariaLabelledby: {
      type: String,
      default: null,
    },
    /** The default active/visible tab, by index. */
    defaultTab: {
      type: Number,
      validator: value => Number.isInteger(value),
      default: 0,
    },
    /** Provide custom css classes for the TabList container */
    customTabListCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Provide custom css classes for the tab buttons */
    customTabCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Provide custom css classes for the selected tab button */
    customTabSelectedCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Provide custom css classes for the container of the tab panels */
    customPanelWrapperCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Provide custom css classes for each tab panel */
    customPanelCssClasses: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedTab: this.defaultTab,
      slottedTabs: [],
      slottedPanels: [],
    }
  },
  methods: {
    getElementId(desc, index) {
      return [this.id, 'tabsComponent', desc, index].join('-')
    },
    setSelectedTab(index, event) {
      this.selectedTab = index
      this.$emit('onTabClick', index, event) // so the parent of this component knows about which tab button clicks
    },
    isSelectedTab(index) {
      return index === this.selectedTab
    },
    setTabIndex(index) {
      return this.isSelectedTab(index) ? 0 : -1
    },
    changeFocus(index, e) {
      // keyboard accessibility. Follows W3 expected manual activation keyboard behaviour
      const tabsLength = this.slottedTabs.length
      const tabs = this.$refs.tabs
      const key = e.key

      if (key === 'ArrowRight') {
        e.preventDefault()
        const nextIndex = (index + 1) % tabsLength
        tabs[nextIndex].focus()
      }
      else if (key === 'ArrowLeft') {
        e.preventDefault()
        const prevIndex = (index - 1 + tabsLength) % tabsLength
        tabs[prevIndex].focus()
      }
      else if (key === 'Home') {
        e.preventDefault()
        const firstIndex = 0
        tabs[firstIndex].focus()
      }
      else if (key === 'End') {
        e.preventDefault()
        const lastIndex = tabsLength ? tabsLength - 1 : 0
        tabs[lastIndex].focus()
      }
    },
    getTabClasses(index) {
      const classes = [
        'bg-science-blue-5',
        'p-[.89em]',
        'border',
        'border-royal-blue-20',
        'text-royal-blue-100',
        'hover:bg-white',
        'focus-visible:relative',
        'focus-visible:outline',
        'focus-visible:outline-2',
        'focus-visible:outline-offset-2',
        'focus-visible:outline-royal-blue-100',
      ]
      classes.push(...this.customTabCssClasses)
      const selectedClasses = ['bg-white', 'border-b-transparent']
      selectedClasses.push(...this.customTabSelectedCssClasses)
      if (this.isSelectedTab(index)) classes.push(...selectedClasses)
      return classes
    },
    getPanelClasses(index) {
      const classes = []
      classes.push(...this.customPanelCssClasses)
      if (this.isSelectedTab(index)) {
        classes.push('block')
      }
      else {
        classes.push('hidden')
      }
      return classes
    },
    processSlot(slot) {
      const processedSlots = []
      for (const fragment of slot) {
        if (
          typeof fragment.type === 'symbol'
          && fragment.type.toString().toLowerCase() === 'symbol(v-fgt)'
          && Array.isArray(fragment.children)
        ) {
          // catches parent fragment that uses v-for, and returns its children as individual fragments for each tab instead. To address Vue 3's handling of consolidated fragments.
          processedSlots.push(...fragment.children)
        }
        else {
          processedSlots.push(fragment)
        }
      }
      return processedSlots
    },
  },
  computed: {
    /** Cache array of ids, for performance. */
    tabIds() {
      return this.slottedTabs.map((_, index) =>
        this.getElementId('tab', index),
      )
    },
    /** Cache array of ids, for performance. */
    panelIds() {
      return this.slottedTabs.map((_, index) =>
        this.getElementId('tabPanel', index),
      )
    },
    tabListClasses() {
      const classes = ['flex', 'relative']
      classes.push(...this.customTabListCssClasses)
      return classes
    },
    panelWrapperCssClasses() {
      const classes = [
        '-mt-px',
        'p-[.89em]',
        'border',
        'border-royal-blue-2',
        'rounded-b',
      ]
      classes.push(...this.customPanelWrapperCssClasses)
      return classes
    },
    ariaLabelAttr() {
      /** aria-labelledby attribute to have precedence over aria-label. */
      if (this.ariaLabelledby !== null) {
        return { 'aria-labelledby': this.ariaLabelledby }
      }
      else {
        return { 'aria-label': this.ariaLabel }
      }
    },
  },
  mounted() {
    const tabsSlot = this.$slots.tabs
    if (tabsSlot) {
      this.slottedTabs = this.processSlot(tabsSlot())
    }
  },
  emits: ['onTabClick'],
}
</script>
